import React, { lazy, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { Flex, useDisclosure } from "@chakra-ui/react";

import { useGetMeQuery } from "redux/services/apiService";
import {
  isUserAuthenticated,
  selectUserPermissions,
  selectUserRole,
} from "redux/slices/user";
import { useAppSelector } from "redux/store";

const Header = lazy(() => import("./Header/Header"));
const MenuBar = lazy(() => import("./MenuBar/MenuBar"));
const Sidebar = lazy(() => import("./Sidebar/Sidebar"));
const SidebarPublic = lazy(() => import("./Sidebar/SidebarPublic"));

const Layout: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const isAuthenticated = useAppSelector(isUserAuthenticated);
  const isAuthPage =
    pathname === "/login" ||
    pathname === "/forgot-password" ||
    pathname.startsWith("/reset-password");

  useGetMeQuery(undefined, {
    skip: isAuthenticated || isAuthPage,
  });
  
  const permissions = useAppSelector(selectUserPermissions);
  const role = useAppSelector(selectUserRole);
  const isAdmin =
    role === "super-admin" || role === "super admin";
  const canViewDatalake =
    permissions?.some((p) => p === "view.datalake") ?? false;
  const canViewDataWarehouse =
    permissions?.some((p) => p === "view.data_warehouse") ?? false;

  const canViewMenus = permissions?.some((p) => p === "view.menus") ?? false;
  const canViewDatasets =
    permissions?.some((p) => p === "view.datasets") ?? false;

  const canViewUsers = permissions?.some((p) => p === "view.users") ?? false;

  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const onToggleSidebar = () => (isOpen ? onClose() : onOpen());

  // ✅ Sidebar hanya untuk halaman back-office & setting
  const isSidebarRoute =
    pathname === "/back-office" || pathname.startsWith("/setting");
  const showSidebar =
    !isAuthPage && isAuthenticated && isSidebarRoute;

  // Sidebar Public untuk halaman selain back-office & setting (Dashboard, CCTV, dll)
  const showSidebarPublic = !isSidebarRoute && pathname.startsWith("/apps/cctv") && pathname !== "/";
  
  // ✅ MenuBar tampil di "/" dan dynamic dashboard "/:parent/:menu/:source"
  const isDynamicDashboardRoute = /^\/[^/]+\/[^/]+\/[^/]+$/.test(pathname);
  const showMenuBar = !isAuthPage && (pathname === "/" || isDynamicDashboardRoute);

  return (
    <Flex direction="column" minH="100vh" bgColor="#EDEFF4">
      {!isAuthPage && (
        <Suspense fallback={<div>Loading...</div>}>
          <Header />
        </Suspense>
      )}

      <Flex flex="1" minH={0}>
        {showSidebar && (
          <Suspense fallback={<div>Loading...</div>}>
            <Sidebar
              isOpen={isOpen}
              onToggle={onToggleSidebar}
              onClose={onClose}
              isAdmin={isAdmin}
              canViewDatalake={canViewDatalake}
              canViewDataWarehouse={canViewDataWarehouse}
              canViewMenus={canViewMenus}
              canViewDatasets={canViewDatasets}
              canViewUsers={canViewUsers}
            />
          </Suspense>
        )}

        {showSidebarPublic && (
          <Suspense fallback={<div>Loading...</div>}>
            <SidebarPublic
              isOpen={isOpen}
              onToggle={onToggleSidebar}
              onClose={onClose}
            />
          </Suspense>
        )}

        {/* Content */}
        <Flex flex="1" flexDirection="column" minW={0}>
          <Outlet />
        </Flex>
      </Flex>

      {showMenuBar && (
        <Suspense fallback={<div>Loading...</div>}>
          <MenuBar />
        </Suspense>
      )}
    </Flex>
  );
};

export default Layout;
